import { z } from "zod";
import { AnnotationSchema, InkStrokeSchema, ShapeAnnotationSchema, TextAnnotationSchema, type Annotation } from "./schema/layers";

/** Deck lifecycle status stored in D1 and broadcast to rooms. */
export const DeckStatusSchema = z.enum(["created", "uploaded", "queued", "converting", "ready", "failed"]);
export type DeckStatus = z.infer<typeof DeckStatusSchema>;

export const MediaSyncSchema = z.object({
  elementId: z.string(),
  /** ms into the media at `serverTime` */
  position: z.number(),
  playing: z.boolean(),
  /** DO-stamped epoch ms */
  serverTime: z.number(),
});
export type MediaSync = z.infer<typeof MediaSyncSchema>;

/* ---------- client -> server ---------- */
export const ClientMessageSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("ping"), t: z.number() }),
  z.object({ type: z.literal("goto"), page: z.number().int().min(0), step: z.number().int().min(-1) }),
  z.object({ type: z.literal("next") }),
  z.object({ type: z.literal("prev") }),
  z.object({
    type: z.literal("media"),
    elementId: z.string(),
    action: z.enum(["play", "pause", "seek", "stop"]),
    position: z.number(),
  }),
  z.object({ type: z.literal("mediaSync"), elementId: z.string(), position: z.number(), playing: z.boolean() }),
  z.object({ type: z.literal("stroke.add"), page: z.string(), stroke: AnnotationSchema }),
  z.object({ type: z.literal("stroke.undo"), page: z.string() }),
  z.object({ type: z.literal("stroke.remove"), page: z.string(), strokeId: z.string() }),
  z.object({ type: z.literal("stroke.clear"), page: z.string() }),
  /** presenter clicked a shape that triggers an interactive animation sequence (index into page.timeline.interactive) */
  z.object({ type: z.literal("trigger"), page: z.number().int(), seq: z.number().int() }),
]);
export type ClientMessage = z.infer<typeof ClientMessageSchema>;

/* ---------- server -> client ---------- */
export const ServerMessageSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("state"),
    page: z.number().int(),
    step: z.number().int(),
    role: z.enum(["operator", "viewer"]),
    viewers: z.number().int(),
    mediaSync: z.array(MediaSyncSchema),
    status: DeckStatusSchema,
    serverTime: z.number(),
    /** the live revision at connect time; a reconnecting client reloads deck.json when it moved on */
    revision: z.number().int().optional(),
  }),
  z.object({ type: z.literal("pong"), t: z.number(), serverTime: z.number() }),
  z.object({ type: z.literal("goto"), page: z.number().int(), step: z.number().int() }),
  z.object({
    type: z.literal("media"),
    elementId: z.string(),
    action: z.enum(["play", "pause", "seek", "stop"]),
    position: z.number(),
    serverTime: z.number(),
  }),
  z.object({ type: z.literal("mediaSync"), sync: MediaSyncSchema }),
  z.object({ type: z.literal("mediaReady"), mediaId: z.string() }),
  z.object({ type: z.literal("stroke.add"), page: z.string(), stroke: AnnotationSchema }),
  z.object({ type: z.literal("stroke.undo"), page: z.string(), strokeId: z.string() }),
  z.object({ type: z.literal("stroke.clear"), page: z.string() }),
  z.object({ type: z.literal("trigger"), page: z.number().int(), seq: z.number().int() }),
  z.object({
    type: z.literal("status"),
    status: DeckStatusSchema,
    progress: z.object({ done: z.number(), total: z.number() }).optional(),
    error: z.string().optional(),
  }),
  z.object({ type: z.literal("presence"), count: z.number().int() }),
  /** a revision was applied: it is the live deck now, reload deck.json */
  z.object({ type: z.literal("deck.changed"), revision: z.number().int() }),
  /** the deck is gone: the stream ends and there is nothing to come back to */
  z.object({ type: z.literal("deck.deleted") }),
  z.object({ type: z.literal("error"), message: z.string() }),
]);
export type ServerMessage = z.infer<typeof ServerMessageSchema>;

/* ---------- REST shapes ---------- */
export interface DeckSummary {
  id: string;
  title: string;
  status: DeckStatus;
  sourceType: "pptx" | "pdf" | "images" | null;
  progress: { done: number; total: number } | null;
  warningCounts: { error: number; warning: number; info: number };
  mediaPending: number;
  deckUrl: string | null;
  error: string | null;
  createdAt: string;
  /** the head: the working copy every edit builds on (bumped by each); send it with edits to make sure nobody changed the deck in between */
  revision: number;
  /** the revision the room shows (deck.json): the head once applied */
  liveRevision: number;
}

export interface CreateDeckResponse {
  deckId: string;
  /** the operator token: uploads, presents, draws, edits the slides and notes, makes API keys, deletes; shown once */
  token: string;
  viewerUrl: string;
  /** the player link that makes its holder the operator */
  operatorUrl: string;
}

/** Answer of making a new token for a deck (the owner's key or the admin): the old one stops at once. */
export interface DeckTokenResponse {
  deckId: string;
  token: string;
  url: string;
}

/** What a token is to a deck: its operator (the deck's token, or its owner's key), or a viewer. */
export type DeckRole = "operator" | "viewer";

/* ---------- public API ---------- */

/** What an API key may do (the operator token does all three): present, draw, and edit (the slides, pictures, notes, revisions). */
export const ApiScopeSchema = z.enum(["present", "draw", "edit"]);
export type ApiScope = z.infer<typeof ApiScopeSchema>;

export const CreateApiKeyRequestSchema = z.object({
  name: z.string().max(100).default(""),
  scopes: z.array(ApiScopeSchema).min(1),
});

export interface ApiKeyInfo {
  id: string;
  name: string;
  scopes: ApiScope[];
  createdAt: string;
  lastUsedAt: string | null;
}

export interface CreateApiKeyResponse extends ApiKeyInfo {
  /** the secret, shown only in this response */
  key: string;
}

/** POST /api/decks/:id/present. Page indexes are 0-based; step -1 means the page with nothing played yet. */
export const PresentRequestSchema = z.discriminatedUnion("action", [
  z.object({ action: z.literal("next") }),
  z.object({ action: z.literal("prev") }),
  z.object({ action: z.literal("first") }),
  z.object({ action: z.literal("last") }),
  z.object({
    action: z.literal("goto"),
    page: z.number().int().min(0).optional(),
    pageId: z.string().optional(),
    step: z.number().int().min(-1).optional(),
  }),
]).refine((v) => v.action !== "goto" || v.page !== undefined || v.pageId !== undefined, "goto needs page or pageId");
export type PresentRequest = z.infer<typeof PresentRequestSchema>;

/** Where the presentation is. Answer of GET and POST /api/decks/:id/present. */
export interface PresentState {
  page: number;
  pageId: string;
  step: number;
  pageCount: number;
  /** steps on the current page */
  stepCount: number;
}

/**
 * A vector drawing sent to POST /api/decks/:id/pages/:page/drawings. The server fills in id
 * (unless given), authorId and createdAt. Images go through the .../drawings/image upload instead.
 */
const serverFilled = { id: true, authorId: true, createdAt: true } as const;
export const DrawInputSchema = z.discriminatedUnion("kind", [
  InkStrokeSchema.omit(serverFilled).extend({ id: z.string().max(100).optional() }),
  ShapeAnnotationSchema.omit(serverFilled).extend({ id: z.string().max(100).optional() }),
  TextAnnotationSchema.omit(serverFilled).extend({ id: z.string().max(100).optional() }),
]);
export type DrawInput = z.infer<typeof DrawInputSchema>;

/** Body of POST .../drawings: one drawing, or several in `annotations`. */
export const DrawRequestSchema = z.union([
  DrawInputSchema,
  z.object({ annotations: z.array(DrawInputSchema).min(1).max(500) }),
]);

/** Answer of the drawings endpoints. */
export interface DrawingsResponse {
  pageId: string;
  annotations: Annotation[];
}

/** GET and PUT /api/decks/:id/pages/:page/notes: the page's speaker notes (the layer's text, or the slide's own when none was saved). */
export interface NotesResponse {
  pageId: string;
  text: string;
}
export const NotesRequestSchema = z.object({ text: z.string().max(100_000) });

/** One saved state of the slides (GET /api/decks/:id/revisions): the conversion is revision 0, every edit or restore the next. */
export interface RevisionInfo {
  revision: number;
  /** when it was saved */
  at: string;
  /** who saved it: "converter", "operator", "user:<id>" or "key:<id>" */
  by: string;
  /** what the edit did, in a few words (the op names and counts, or "restored revision N") */
  summary: string;
  /** the working copy: what the next edit builds on */
  head: boolean;
  /** what the room shows */
  live: boolean;
}

/* ---------- users and plans (admin API and /api/me) ---------- */

/**
 * A plan is a named set of limits; null means no limit. A user has one plan, a plan many users. A user
 * is whoever holds a key: a person, a script, or a deckloom.io account (which links to its user by the key).
 */
export const PlanLimitsSchema = z.object({
  maxDecks: z.number().int().positive().nullable().default(null),
  /** bytes per uploaded file (the platform's own cap still applies) */
  maxUploadBytes: z.number().int().positive().nullable().default(null),
  /** slides per deck, at conversion and through the edit API */
  maxPages: z.number().int().positive().nullable().default(null),
  /** viewers in one room at a time (the presenter always gets in) */
  maxViewers: z.number().int().positive().nullable().default(null),
  /** public API writes limited to 600 a minute per deck and caller */
  rateLimited: z.boolean().default(true),
  /** APIs the plan's decks may use */
  scopes: z.array(ApiScopeSchema).default(["present", "draw", "edit"]),
  /** bytes of converted files (slides, media, drawings) all the user's decks may hold together */
  maxStorageBytes: z.number().int().positive().nullable().default(null),
  /** decks with a presenter connected at the same time */
  maxLiveDecks: z.number().int().positive().nullable().default(null),
});
export type PlanLimits = z.infer<typeof PlanLimitsSchema>;

export const PlanInputSchema = PlanLimitsSchema.extend({
  /** short slug; default made from the name */
  id: z.string().regex(/^[a-z0-9][a-z0-9-]{0,39}$/, "lower-case letters, digits and dashes").optional(),
  name: z.string().min(1).max(100),
});
export const PlanPatchSchema = PlanLimitsSchema.partial().extend({ name: z.string().min(1).max(100).optional() });

export interface Plan extends PlanLimits {
  id: string;
  name: string;
  createdAt: string;
}

export const UserInputSchema = z.object({ name: z.string().min(1).max(100), planId: z.string() });
export const UserPatchSchema = z.object({ name: z.string().min(1).max(100).optional(), planId: z.string().optional(), disabled: z.boolean().optional() });

export interface UserInfo {
  id: string;
  name: string;
  planId: string;
  createdAt: string;
  disabled: boolean;
}

/** GET /api/admin/plans: a plan and how many users hold it. */
export interface AdminPlan extends Plan {
  users: number;
}

/** GET /api/admin/decks: a deck and whose it is. */
export interface AdminDeck extends DeckSummary {
  ownerId: string | null;
}

/** GET /api/admin/users: a user, how many decks it owns and the bytes they hold. */
export interface AdminUser extends UserInfo {
  decks: number;
  storageBytes: number;
}

/* ---------- deckloom.io: accounts, their people and plans (its own API; the admin site reaches it through deckloom.io's admin API) ---------- */

/** A deckloom.io plan: what an account's sessions may do (null = no limit). */
export const IoPlanLimitsSchema = z.object({
  /** sessions running at once */
  maxSessions: z.number().int().positive().nullable().default(null),
  /** minutes a session may run; it ends by itself after that */
  maxSessionMinutes: z.number().int().positive().nullable().default(null),
  /** people in a session at once, the host included */
  maxParticipants: z.number().int().positive().nullable().default(null),
  /** microphone and camera tracks live in a session at once */
  maxMediaTracks: z.number().int().positive().nullable().default(null),
  /** may the host share a screen */
  screenShare: z.boolean().default(true),
  /** may the host put form pages on stage */
  forms: z.boolean().default(true),
});
export type IoPlanLimits = z.infer<typeof IoPlanLimitsSchema>;
export const IoPlanInputSchema = IoPlanLimitsSchema.extend({
  id: z.string().regex(/^[a-z0-9][a-z0-9-]{0,39}$/, "lower-case letters, digits and dashes").optional(),
  name: z.string().min(1).max(100),
});
export const IoPlanPatchSchema = IoPlanLimitsSchema.partial().extend({ name: z.string().min(1).max(100).optional() });
export interface IoPlan extends IoPlanLimits {
  id: string;
  name: string;
  createdAt: string;
  /** how many accounts hold it */
  accounts: number;
}

/** How the people of an account may sign in to deckloom.io; the account's admins choose. Email OTP is the one built so far. */
export const LoginMethodSchema = z.enum(["email-otp", "email-password", "google", "facebook", "sso"]);
export type LoginMethod = z.infer<typeof LoginMethodSchema>;

/** A person on deckloom.io, as a member of one account. */
export interface IoUser {
  id: string;
  email: string;
  name: string;
  role: "admin" | "member";
  createdAt: string;
  lastLoginAt: string | null;
}

export const IoAccountInputSchema = z.object({
  id: z.string().regex(/^[a-z0-9][a-z0-9-]{0,39}$/, "lower-case letters, digits and dashes").optional(),
  name: z.string().min(1).max(100),
  planId: z.string(),
  /** the deckloom.net user key the account uploads decks with; none = screen share only */
  deckloomKey: z.string().max(200).nullable().optional(),
  public: z.boolean().default(false),
  /** the first admin's email */
  adminEmail: z.string().optional(),
});
export const IoAccountPatchSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  planId: z.string().optional(),
  deckloomKey: z.string().max(200).nullable().optional(),
  public: z.boolean().optional(),
  loginMethods: z.array(LoginMethodSchema).min(1).optional(),
  disabled: z.boolean().optional(),
});

/** An account on deckloom.io: people, a plan for its sessions, a page, and maybe a deckloom.net user key for decks. */
export interface IoAccount {
  id: string;
  name: string;
  planId: string;
  /** whether a deckloom.net key is set (the key itself is never answered) */
  hasDeckloomKey: boolean;
  /** the deckloom.net user id the key belongs to, when known */
  deckloomUserId: string | null;
  public: boolean;
  loginMethods: LoginMethod[];
  users: number;
  disabled: boolean;
  createdAt: string;
}

/** GET /api/admin/mail: a message info@ received (box 'in') or the admin sent (box 'out'), without its body. */
export interface MailInfo {
  id: string;
  box: "in" | "out";
  from: string;
  to: string;
  /** the From header as written, e.g. "Someone <someone@example.com>" (received mail) */
  fromHeader: string | null;
  subject: string;
  /** bytes of the received message */
  size: number;
  /** sent mail: the received message it answers */
  inReplyTo: string | null;
  /** sent mail: whether Cloudflare took it for delivery */
  status: "pending" | "accepted" | "failed" | null;
  error: string | null;
  createdAt: string;
  read: boolean;
  attachments: number;
}

/** GET /api/admin/mail/:id: the message with its text and files (GET /api/admin/mail/:id/files/:fileId). */
export interface MailDetail extends MailInfo {
  text: string | null;
  html: string | null;
  files: { id: string; filename: string; mime: string; bytes: number }[];
}

/** Answer of creating a user or rotating its key; `key` is shown only here. */
export interface UserKeyResponse extends UserInfo {
  key: string;
}

/** GET /api/me: the user behind the key, its plan and what it uses. */
export interface MeResponse {
  user: UserInfo;
  plan: Plan;
  usage: { decks: number; storageBytes: number; liveDecks: number };
}
